import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {boolean} */
  #prefetching = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    // Fallback for contexts without a <product-card> ancestor (e.g. full-search resource cards)
    const resourceCardLink = /** @type {HTMLAnchorElement | null} */ (
      this.closest('.resource-card')?.querySelector('.resource-card__link')
    );
    const productLink =
      productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink() || resourceCardLink;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Prefetch AND cache the product page (best-effort). Called by product-card.js on
   * card hover/focus and variant change, so a later click hits the cache in
   * `handleClick` and opens with no fetch latency. Unlike `fetchProductPage` (which
   * only returns the HTML), this stores the parsed grid in `#cachedContent`.
   */
  prefetch = async () => {
    const currentUrl = this.productPageUrl;
    // Skip if already cached or a prefetch is already in flight (a second hover/focus
    // event would otherwise abort the first request via the shared AbortController).
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#prefetching) return;

    this.#prefetching = true;
    try {
      const html = await this.fetchProductPage(currentUrl);
      if (!html) return;
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (gridElement && !this.#cachedContent.has(currentUrl)) {
        this.#cachedContent.set(currentUrl, /** @type {Element} */ (gridElement.cloneNode(true)));
      }
    } catch {
      // Best-effort; the click will fetch again if this failed/aborted.
    } finally {
      this.#prefetching = false;
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      this.#buildMobileThumb(productGrid);
    } else {
      this.#linkDesktopContentToPdp(productGrid);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Aristopet mobile bottom-sheet header. We KEEP the full `.product-details` intact — the
   * variant picker, add-to-cart form, quantity stepper, stock and wishlist are the real PDP
   * components and stay fully functional (unlike the old branch, which rebuilt a bare header
   * and dropped stock/wishlist). We only prepend a compact product thumbnail to the info
   * column; `quick-add-modal-styles` handles the Figma sheet layout and hides the full media
   * gallery on mobile.
   * @param {Element} productGrid - The cloned product grid about to be morphed in
   */
  #buildMobileThumb(productGrid) {
    const column = productGrid.querySelector('.product-details .group-block-content');
    if (!column || column.querySelector('.quick-add-modal__header')) return;

    // Figma header: [thumb 100×125] | [info: vendor+title group, then price] — an explicit
    // flex row so the price stays beside the thumbnail (a float lets it drop below). We only
    // MOVE existing nodes (vendor+title group + price) into the info column; the price is
    // still the live <product-price>, so variant-change updates keep working.
    const infoGroup = /** @type {Element | null} */ (column.querySelector(':scope > .group-block'));
    const price = /** @type {Element | null} */ (column.querySelector(':scope > product-price'));
    const firstImage = /** @type {HTMLImageElement | null} */ (
      productGrid.querySelector('.product-information__media img')
    );

    const productUrl = this.productPageUrl;

    const header = document.createElement('div');
    header.className = 'quick-add-modal__header';

    if (firstImage) {
      const thumb = document.createElement('div');
      thumb.className = 'quick-add-modal__thumb';
      thumb.setAttribute('aria-hidden', 'true');
      const image = /** @type {HTMLImageElement} */ (firstImage.cloneNode(true));
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.loading = 'eager';
      // Link the thumbnail to the PDP. It's decorative (the title link below is the
      // accessible one), so keep it out of the tab order and the a11y tree.
      if (productUrl) {
        const thumbLink = document.createElement('a');
        thumbLink.className = 'quick-add-modal__thumb-link';
        thumbLink.href = productUrl;
        thumbLink.tabIndex = -1;
        thumbLink.appendChild(image);
        thumb.appendChild(thumbLink);
      } else {
        thumb.appendChild(image);
      }
      header.appendChild(thumb);
    }

    const info = document.createElement('div');
    info.className = 'quick-add-modal__header-info';
    if (infoGroup) info.appendChild(infoGroup);
    if (price) info.appendChild(price);
    header.appendChild(info);

    // Link the title to the PDP without changing its look: wrap the heading's
    // contents in an anchor that inherits color and drops the underline (see CSS).
    if (productUrl) {
      const titleHeading = info.querySelector('.text-block h1') ?? info.querySelector('h1, h2');
      if (titleHeading && !titleHeading.querySelector('a')) {
        const titleLink = document.createElement('a');
        titleLink.className = 'quick-add-modal__title-link';
        titleLink.href = productUrl;
        while (titleHeading.firstChild) titleLink.appendChild(titleHeading.firstChild);
        titleHeading.appendChild(titleLink);
      }
    }

    column.insertBefore(header, column.firstChild);
  }

  /**
   * Desktop popup: link ONLY the product title to the PDP without changing its look.
   * The gallery image is intentionally NOT linked — it keeps its zoom behavior, which
   * a wrapping anchor would break.
   * @param {Element} productGrid - The cloned product grid about to be morphed in
   */
  #linkDesktopContentToPdp(productGrid) {
    const productUrl = this.productPageUrl;
    if (!productUrl) return;

    const titleHeading = productGrid.querySelector('.product-details .text-block h1');
    if (titleHeading && !titleHeading.querySelector('a')) {
      const titleLink = document.createElement('a');
      titleLink.className = 'quick-add-modal__title-link';
      titleLink.href = productUrl;
      while (titleHeading.firstChild) titleLink.appendChild(titleHeading.firstChild);
      titleHeading.appendChild(titleLink);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
